import type { FormEvent } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import type { ErrorMessageMap } from '../../../../components/FormInput/types';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { ForceResetError } from '../../../../features/auth/forceReset/ForceResetError';
import { RedesignEmailAndPasswordInputs } from '../../../../features/auth/redesign-components/RedesignEmailAndPasswordInputs/RedesignEmailAndPasswordInputs';
import { ThirdPartyAuth } from '../../../../features/auth/ThirdPartyAuth/ThirdPartyAuth';
import { analytics } from '../../../../services/analytics';

import { useAuthForm } from './hooks/useAuthForm';
import classes from './SignupSection.module.scss';

interface LoginForm extends HTMLFormElement {
  elements: HTMLFormControlsCollection & {
    email: HTMLInputElement;
    password: HTMLInputElement;
  };
}

const getLoginEmailErrorMessageMap = (t: TFunction): ErrorMessageMap => ({
  typeMismatch: t('validation.emailInvalid'),
  valueMissing: t('validation.emailRequired'),
});

const getLoginPasswordErrorMessageMap = (t: TFunction): ErrorMessageMap => ({
  valueMissing: t('validation.passwordRequired'),
});

interface LoginFormProps {
  onLoginSuccess?: () => void;
  onLoginNeedsVerification?: () => void;
  onSwitchToSignup: () => void;
  wizardProps?: WizardChildProps;
}

export const LoginForm = observer(({ wizardProps, onLoginNeedsVerification, onSwitchToSignup }: LoginFormProps) => {
  const { t } = useTranslation();
  const {
    error,
    isLoading,
    landingKey,
    onEmailChange,
    onPasswordChange,
    handleAuth,
    isForceReset,
    startResetPassword,
  } = useAuthForm(onLoginNeedsVerification);

  const handleLogin = (event: FormEvent<LoginForm>) => {
    event.preventDefault();
    event.stopPropagation();

    const { email, password } = event.currentTarget.elements;

    void analytics.sendEvent('log_in', {
      step_name: 'log_in_platform',
      event_action: 'click',
      click_id: CLICK_IDS.AUTH_BUTTONS.LOG_IN.email,
      log_in_platform: 'email',
      triggered_by: 'log_in_platform',
      landingKey,
    });

    void handleAuth({
      provider: 'email',
      action: 'log_in',
      credentials: {
        email: email.value,
        password: password.value,
      },
    });
  };

  const openResetPassword = () => {
    void analytics.sendEvent('log_in', {
      step_name: 'forgot_password',
      event_action: 'click',
      click_id: CLICK_IDS.AUTH_FLOW.START_RESET_PASSWORD,
      landingKey,
    });

    startResetPassword(() => wizardProps?.forceStep(1));
  };

  return (
    <div className={classes.signupSection}>
      <div className={classes.googleSignupWrapper}>
        <ThirdPartyAuth
          action="Log in"
          className={classes.thirdPartyAuth}
          excludedProviders={['facebook', 'apple']}
          hideLoader={true}
        />
      </div>

      <div className={classes.orDivider}>
        <span className={classes.orText}>{t('common.or')}</span>
      </div>

      <form className={classes.emailFormWrapper} id="login-form" onSubmit={handleLogin}>
        <RedesignEmailAndPasswordInputs
          disabled={isLoading}
          emailErrorMessageMap={getLoginEmailErrorMessageMap(t)}
          passwordErrorMessageMap={getLoginPasswordErrorMessageMap(t)}
          type="log_in"
          onChangeEmail={onEmailChange}
          onChangePassword={onPasswordChange}
        />
      </form>

      {isForceReset ? (
        <ForceResetError message={error} onResetPassword={openResetPassword} />
      ) : (
        <>
          <div className={classes.loginLink} data-testid="direct-login-forgot-password-line">
            <Trans
              components={{
                question: <span />,
                reset: <span className={classes.loginLinkText} onClick={openResetPassword} />,
              }}
              i18nKey="landing.direct.forgotPasswordResetPassword"
            />
          </div>

          <ErrorMessage noSpaceWhenEmpty className={classes.authErrorMessage}>
            {error}
          </ErrorMessage>
        </>
      )}

      <Button
        btnType="main"
        className={classNames(classes.createAccountButton, classes.loginButton)}
        disabled={isLoading || isForceReset}
        form="login-form"
        type="submit"
      >
        {t('landing.logIn')}
      </Button>

      <div className={classNames(classes.loginLink, classes.spacedLink)} data-testid="direct-login-signup-line">
        <Trans
          components={{
            question: <span />,
            signup: <span className={classes.loginLinkText} onClick={onSwitchToSignup} />,
          }}
          i18nKey="auth.noAccountYetSignUp"
        />
      </div>
    </div>
  );
});
